import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import LockResetIcon from "@mui/icons-material/LockReset";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import PrintIcon from "@mui/icons-material/Print";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ContextSession } from "../../services/ContextSession";
import { useNavigate } from "react-router-dom";

const API_FALLBACK = "https://bei360.injupemp.gob.hn:8444/";
//const API_FALLBACK = "http://127.0.0.1:8000/";

const ACTIONS = [
  {
    id: "1",
    title: "Activar usuario",
    description: "Habilita nuevamente un usuario en AS400.",
    field: "username",
    fieldLabel: "Usuario AS400",
    fieldHelper: "Ingresa el usuario que se debe activar.",
    icon: <PersonAddAlt1Icon fontSize="small" />,
    accent: "#1d4ed8",
  },
  {
    id: "2",
    title: "Activar y resetear clave",
    description: "Activa el usuario y genera una clave temporal.",
    field: "username",
    fieldLabel: "Usuario AS400",
    fieldHelper: "Ingresa el usuario al que se le reseteara la clave.",
    icon: <LockResetIcon fontSize="small" />,
    accent: "#0f766e",
  },
  {
    id: "3",
    title: "Activar dispositivo",
    description: "Ejecuta la gestón de habilitacion para un dispositivo.",
    field: "dispositivo",
    fieldLabel: "Dispositivo",
    fieldHelper: "Ingresa el identificador del dispositivo.",
    icon: <DevicesOtherIcon fontSize="small" />,
    accent: "#7c3aed",
  },
  {
    id: "4",
    title: "Activar impresora",
    description: "Activa una impresora dentro del entorno AS400.",
    field: "impresora",
    fieldLabel: "Impresora",
    fieldHelper: "Ingresa el nombre de la impresora.",
    icon: <PrintIcon fontSize="small" />,
    accent: "#b45309",
  },
  {
    id: "5",
    title: "Borrar mensajes de impresora",
    description: "Limpia mensajes pendientes asociados a la impresora.",
    field: "impresora",
    fieldLabel: "Impresora",
    fieldHelper: "Ingresa la impresora de la que se borraran mensajes.",
    icon: <CleaningServicesIcon fontSize="small" />,
    accent: "#be123c",
  },
];

function getFieldValue(field, values) {
  if (field === "username") return values.username;
  if (field === "dispositivo") return values.dispositivo;
  if (field === "impresora") return values.impresora;
  return "";
}

function buildRequestBody(selectedAction, values) {
  const formData = new FormData();
  formData.append("tipo", selectedAction);

  if (selectedAction === "1" || selectedAction === "2") {
    formData.append("username", values.username.trim().toUpperCase());
  }

  if (selectedAction === "3") {
    formData.append("dispositivo", values.dispositivo.trim().toUpperCase());
  }

  if (selectedAction === "4" || selectedAction === "5") {
    formData.append("impresora", values.impresora.trim().toUpperCase());
  }

  return formData;
}

function buildRequestError(response, data, fallbackMessage) {
  const error = new Error(
    data?.error || data?.message || fallbackMessage || "No se pudo completar la gestion en AS400.",
  );
  error.status = response.status;
  error.code = data?.code;
  error.payload = data;
  return error;
}

async function ejecutarGestion({
  apiBaseUrl,
  accessToken,
  selectedAction,
  values,
}) {
  const response = await fetch(`${apiBaseUrl}api/reset/users/as400/`, {
    method: "POST",
    body: buildRequestBody(selectedAction, values),
    headers: {
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildRequestError(response, data, "No se pudo completar la gestion en AS400.");
  }

  if (!response.ok) {
    throw new Error(
      data?.error ||
        data?.message ||
        "No se pudo completar la gestión en AS400.",
    );
  }

  return data;
}

function ActionCard({ action, selected, onSelect }) {
  return (
    <ButtonBase
      onClick={() => onSelect(action.id)}
      sx={{
        width: "100%",
        borderRadius: 3,
        textAlign: "left",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: selected ? action.accent : "divider",
          bgcolor: selected ? `${action.accent}12` : "background.paper",
          transition: "all 0.2s ease",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: selected ? action.accent : "action.hover",
              color: selected ? "white" : "text.secondary",
              flexShrink: 0,
            }}
          >
            {action.icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={700}>{action.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {action.description}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </ButtonBase>
  );
}

export default function As400() {
  const sessi = React.useContext(ContextSession);
  const navigate = useNavigate();
  const [selectedAction, setSelectedAction] = React.useState("2");
  const [values, setValues] = React.useState({
    username: "",
    dispositivo: "",
    impresora: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");
  const [lastPayload, setLastPayload] = React.useState(null);

  const currentAction = React.useMemo(
    () => ACTIONS.find((item) => item.id === selectedAction) || ACTIONS[0],
    [selectedAction],
  );

  const currentFieldValue = getFieldValue(currentAction.field, values);
  const canSubmit = currentFieldValue.trim().length >= 2;

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleActionChange = (actionId) => {
    setSelectedAction(actionId);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) return;

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const token = sessi?.getToken ? sessi.getToken() : "";
      const apiBaseUrl = API_FALLBACK;

      const data = await ejecutarGestion({
        apiBaseUrl,
        accessToken: token,
        selectedAction,
        values,
      });

      const normalizedValue = currentFieldValue.trim().toUpperCase();
      setLastPayload({
        action: currentAction.title,
        value: normalizedValue,
      });
      setSuccessMsg(data?.message || "Gestión ejecutada correctamente.");
    } catch (error) {
      if (error?.status === 401 && error?.code === "token_not_valid") {
        sessi.clearSession();
        navigate("/");
        return;
      }
      setErrorMsg(error.message || "Ocurrió un error inesperado.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setValues({
      username: "",
      dispositivo: "",
      impresora: "",
    });
    setSelectedAction("2");
    setErrorMsg("");
    setSuccessMsg("");
    setLastPayload(null);
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) 360px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3.5 },
            borderRadius: 4,
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" fontWeight={900}>
                  Gestión AS400
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Ejecuta activacion, reseteo y gestiones operativas de AS400
                  desde UTI.
                </Typography>
              </Box>

              <Chip label="UTI" color="primary" variant="outlined" />
            </Stack>

            <Divider />

            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
                Tipo de gestión
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {ACTIONS.map((action) => (
                  <ActionCard
                    key={action.id}
                    action={action}
                    selected={action.id === selectedAction}
                    onSelect={handleActionChange}
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
                {successMsg ? (
                  <Alert severity="success">{successMsg}</Alert>
                ) : null}

                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                    Datos de la gestión
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label={currentAction.fieldLabel}
                    value={currentFieldValue}
                    onChange={handleChange(currentAction.field)}
                    helperText={currentAction.fieldHelper}
                    placeholder={`Ejemplo: ${currentAction.field === "username" ? "JUSUARIO" : currentAction.field === "dispositivo" ? "DSP001" : "PRT001"}`}
                  />
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Limpiar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!canSubmit || loading}
                    sx={{ minWidth: 220 }}
                  >
                    {loading ? (
                      <CircularProgress size={22} sx={{ color: "white" }} />
                    ) : (
                      "Ejecutar gestión"
                    )}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 4,
            position: { lg: "sticky" },
            top: { lg: 88 },
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>
              Resumen
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={currentAction.title} />
              <Chip label={currentAction.fieldLabel} variant="outlined" />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {currentAction.description}
            </Typography>

            <Divider />

            {lastPayload ? (
              <>
                <Divider />
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                  <Typography fontWeight={700}>Ultima gestión</Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {lastPayload.action}
                </Typography>
                <Typography fontWeight={700}>{lastPayload.value}</Typography>
              </>
            ) : null}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
